import { Injectable } from "@angular/core";
import {
	AzureCommunicationTokenCredential,
} from "@azure/communication-common";
import {
	CallClient,
	DeviceManager,
	LocalVideoStream,
	RemoteVideoStream,
	VideoStreamRenderer,
	TeamsCallAgent,
	TeamsCall,
	DeviceAccess,
	CallState,
} from "@azure/communication-calling";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { ApiService } from "../api/api.service";

@Injectable({
	providedIn: "root",
})
export class CommunicationServiceService {
	private callClient: CallClient;
	private callAgent: TeamsCallAgent;
	private deviceManager: DeviceManager;
	private call: TeamsCall;
	private localVideoStream: LocalVideoStream;
	private callStateSubject = new BehaviorSubject<string>(null);
	public callState$ = this.callStateSubject.asObservable();
	public isInitialized: boolean;
	private permissionRetryCount: number = 0;
	private maxPermissionRetries: number = 3;

	constructor(private apiService: ApiService) {}

	async initialize(connector: string): Promise<boolean> {
	
		if (this.isInitialized) {
			return true;
		}
	
		if (this.call) {
			return false;
		}
	
		try {
			this.callClient = new CallClient();
	
			const teamsAccessTokenResponse: any = await firstValueFrom(
				this.apiService.getTeamAccessToken(connector)
			);
	
			if (!teamsAccessTokenResponse || !teamsAccessTokenResponse.token) {
				return false;
			}
	
			const teamsAccessToken: string = teamsAccessTokenResponse.token;

			await this.createCallAgent(teamsAccessToken);
	
			this.deviceManager = await this.callClient.getDeviceManager();

			// Request device permissions (audio only for now)
			const deviceAccess: DeviceAccess = await this.deviceManager.askDevicePermission({
				video: false, audio: true,
			});
			if (!deviceAccess.audio) {
				if (this.permissionRetryCount >= this.maxPermissionRetries) {
					this.permissionRetryCount = 0;
					return false;
				}
				this.permissionRetryCount++;
				await this.endCall();
				return this.initialize(connector);
			}
	
			this.permissionRetryCount = 0;
			this.isInitialized = true;
			return true;
		} catch (error) {
			return false;
		}
	}

	/**
	 * Creates a TeamsCallAgent using the Teams access token.
	 * @param token Teams access token.
	 */
	async createCallAgent(token: string): Promise<void> {
		try {
			const tokenCredential = new AzureCommunicationTokenCredential(token);
			this.callAgent = await this.callClient.createTeamsCallAgent(tokenCredential);			
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Starts a call to a Teams user.
	 * @param userId User ID of the Teams user to call.
	 */
	async startCall(id: string, type: "User" | "Resource"): Promise<void> {
		try {
			// Check if the CallAgent is initialized
			if (!this.callAgent) {
				throw new Error(
					"CallAgent is not initialized. Ensure the initialize() method has completed successfully."
				);
			}

			this.localVideoStream = await this.getLocalVideoStream();
			if (!this.localVideoStream) {
				throw new Error("No local video stream available.");
			}

			this.call = this.callAgent.startCall(
				//use either microsoftTeamsUserId for a user or for a CallQueue/AutoAttendant use teamsAppId
				type == "User"
					? {
							microsoftTeamsUserId: id,
							isAnonymous: false,
					  }
					: { teamsAppId: id },
				{
					videoOptions: this.localVideoStream
						? { localVideoStreams: [this.localVideoStream] }
						: undefined,
				}
			) as TeamsCall;

			this.setupCallListeners();

		} catch (error) {
			throw error;
		}
	}

	/**
	 * Sets up event listeners for the call.
	 */
	private setupCallListeners(): void {
		this.call.on('stateChanged', () => {
			let newState = this.call.state; 
	
			if (newState === 'Disconnected') {
				const callEndReason = this.call.callEndReason;
				const callEndCode = callEndReason?.code ?? 0;

				if (callEndCode === 603) {
					newState = "Declined" as CallState;
				}
			
				else if (callEndCode !== 0) {
					newState = "Error" as CallState;
				}
			}
	
			this.callStateSubject.next(newState); 
		});

		this.call.on("remoteParticipantsUpdated", (e) => {
			e.added.forEach((participant) => {
				this.subscribeToParticipantVideoStreams(participant);
			});
		});

		this.call.remoteParticipants.forEach((participant) => {
			this.subscribeToParticipantVideoStreams(participant);
		});
	}

	/**
	 * Subscribes to video streams of a participant.
	 * @param participant Remote participant in the call.
	 */
	private subscribeToParticipantVideoStreams(participant): void {
		participant.videoStreams.forEach((remoteVideoStream) => {
			this.subscribeToRemoteVideoStream(remoteVideoStream);
		});

		participant.on("videoStreamsUpdated", (e) => {
			e.added.forEach((remoteVideoStream) => {
				this.subscribeToRemoteVideoStream(remoteVideoStream);
			});
		});
	}

	/**
	 * Subscribes to a remote video stream and renders it.
	 * @param remoteVideoStream Remote video stream from a participant.
	 */
	private async subscribeToRemoteVideoStream(
		remoteVideoStream: RemoteVideoStream
	): Promise<void> {
		const videoStreamRenderer = new VideoStreamRenderer(remoteVideoStream);
		let view;

		const renderVideo = async () => {
			try {
				view = await videoStreamRenderer.createView();
				const container = document.getElementById("videoContainer");
				if (container && view) {
					container.appendChild(view.target);
				}
			} catch (e) {}
		};

		remoteVideoStream.on("isAvailableChanged", async () => {
			if (remoteVideoStream.isAvailable) {
				await renderVideo();
			} else if (view) {
				view.dispose();
				view = undefined;
			}
		});

		if (remoteVideoStream.isAvailable) {
			await renderVideo();
		}
	}

	/**
	 * Sets up the default microphone and speaker devices.
	 */
	private async setupDevices(): Promise<boolean> {
		try {

			const isSelectable: boolean = this.deviceManager.isSpeakerSelectionAvailable

			// TODO: IF NEEDED CHECK FOR SELECTING AN OUTPUT AND IS PWA
			if (!isSelectable) return true

			// Select default microphone
			const microphones = await this.deviceManager.getMicrophones();
			for (const mic of microphones) {
				if (mic.isSystemDefault) {
					//await this.deviceManager.selectMicrophone(mic);
					break;
				}
			}

			// Select default speaker
			const speakers = await this.deviceManager.getSpeakers();
			for (const speaker of speakers) {
				if (speaker.isSystemDefault) {
					//await this.deviceManager.selectSpeaker(speaker);
					break;
				}
			}

			return true;
		} catch (error) {
			return false;
		}
	}

	/**
	 * Starts sending local video stream.
	 */
	async startVideo(): Promise<void> {
		if (!this.localVideoStream) {
			this.localVideoStream = await this.getLocalVideoStream();
		}
		if (this.localVideoStream && this.call) {
			await this.call.startVideo(this.localVideoStream);
		}
	}

	/**
	 * Stops sending local video stream.
	 */
	async stopVideo(): Promise<void> {
		if (this.call && this.localVideoStream) {
			await this.call.stopVideo(this.localVideoStream);
		}
	}

	/**
	 * Mutes the local microphone.
	 */
	async mute(): Promise<void> {
		if (this.call) {
			await this.call.mute();
		}
	}

	/**
	 * Unmutes the local microphone.
	 */
	async unmute(): Promise<void> {
		if (this.call) {
			await this.call.unmute();
		}
	}

	/**
	 * Ends the current call.
	 */
	async endCall(): Promise<void> {
		if (this.call != null) {
			this.callStateSubject.next(null);
			if (this.call.state !== 'Disconnected') {
				try {
					await this.call.hangUp();
				} catch (error) {}
			}
	
			this.call.remoteParticipants.forEach((participant) => {
				this.disposeParticipantVideoStreams(participant);
			});
	
			try {
				this.call.dispose();
			} catch (error) {}
	
			this.call = null;
		}
	
		if (this.callAgent) {
			try {
				await this.callAgent.dispose();
			} catch (error) {}
			
			finally {
				this.callAgent = null;
			}
		}
	
		this.isInitialized = false;
	}
	
	private disposeParticipantVideoStreams(participant): void {
		participant.videoStreams.forEach((remoteVideoStream) => {
			if (remoteVideoStream.isAvailable) {
				remoteVideoStream.dispose();
			}
		});
	}

	public resetCallState(): void {
		this.callStateSubject.next(null);
	}
	
	/**
	 * Retrieves the local video stream from the default camera.
	 * @returns LocalVideoStream instance or undefined if no camera is found.
	 */
	private async getLocalVideoStream(): Promise<LocalVideoStream | undefined> {
		const cameras = await this.deviceManager.getCameras();
		if (cameras && cameras.length > 0) {
			const camera = cameras[0];
			return new LocalVideoStream(camera);
		} else {
			return undefined;
		}
	}
}
